(() => {
  'use strict';

  const ROOT_SELECTOR = [
    '.mc-ref-primary__media img',
    '.mc-ref-mini-media img',
    '.collection_card_img img',
    '.collection_list_img img:not(.secondary_hover)',
    '.collection_slider_img img',
    '.multiple_collections_img img',
    '.mc_img img',
    '.card__media img',
    '.product__media img',
    '.product-media img',
    '.image-with-text img',
    '.image_gallery img',
    '.image-gallery img',
    '.main-collection img',
    '.collection-hero img',
    'main .shopify-section img'
  ].join(',');

  const SKIP_SELECTOR = [
    'header img',
    '.section-header img',
    'footer img',
    '.section-footer img',
    '.menu-drawer img',
    '.mobile_menu img',
    '.cart-drawer img',
    '.predictive-search img',
    '.payment-icon',
    '.icon',
    '.logo',
    '.flag',
    '[data-marri-focus-skip]'
  ].join(',');


  const FORCE_RIGHT_HANDLES = [
    'voila', 'voilà', 'collezione-python', 'python', 'bamboo', 'borchie'
  ];

  function normalized(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function forcedFocusPct(img) {
    const link = img.closest('a[href]');
    const media = img.closest('.mc-ref-primary,.mc-ref-mini-card,.collection_list_block,.collection-card,.card-wrapper,.product-card-wrapper');
    const haystack = normalized([
      img.alt,
      img.title,
      img.currentSrc,
      img.src,
      link && link.getAttribute('href'),
      media && media.getAttribute('data-collection-handle'),
      media && media.getAttribute('data-marri-collection')
    ].filter(Boolean).join(' '));

    // Python needs a slightly less extreme right crop than the other three collections.
    if (haystack.includes('python') || haystack.includes('collezione-python')) return 90;

    const locked = img.closest('[data-marri-focus-lock="right"]');
    if (locked) return 100;
    return FORCE_RIGHT_HANDLES.some((token) => haystack.includes(normalized(token))) ? 100 : null;
  }

  const analysisCache = new Map();
  const applied = new WeakMap();
  const CENTER_RE = /^(?:center(?:\s+center)?|50(?:\.0+)?%\s+50(?:\.0+)?%)$/i;

  function inlineAuthoredFocus(img) {
    const style = img.getAttribute('style') || '';
    const match = style.match(/--marri-focus\s*:\s*([^;]+)/i);
    if (!match) return '';
    return match[1].trim();
  }

  function hasRealFocalPoint(img) {
    const raw = inlineAuthoredFocus(img);
    return raw && !CENTER_RE.test(raw);
  }

  function eligible(img) {
    if (!(img instanceof HTMLImageElement)) return false;
    if (img.matches(SKIP_SELECTOR) || img.closest(SKIP_SELECTOR)) return false;
    if (!img.naturalWidth || !img.naturalHeight) return false;
    if (img.naturalWidth < 280 || img.naturalHeight < 180) return false;

    const sourceRatio = img.naturalWidth / img.naturalHeight;
    if (sourceRatio < 1.12) return false; // problem is primarily horizontal imagery

    const rect = img.getBoundingClientRect();
    if (rect.width < 80 || rect.height < 80) return false;

    const targetRatio = rect.width / rect.height;
    // Only intervene when cover actually removes a meaningful horizontal slice.
    if (targetRatio >= sourceRatio * 0.94) return false;

    const fit = getComputedStyle(img).objectFit;
    if (fit !== 'cover' && !img.closest('.mc-ref-primary__media,.mc-ref-mini-media,.collection_card_img,.collection_list_img,.collection_slider_img,.multiple_collections_img,.mc_img,.card__media')) return false;
    return true;
  }

  function apply(img, xPct) {
    const focus = `${xPct.toFixed(1)}% 50%`;
    img.style.setProperty('--marri-focus', focus, 'important');
    img.style.setProperty('object-position', focus, 'important');
    img.dataset.marriSmartFocus = xPct < 38 ? 'left' : xPct > 62 ? 'right' : 'center';
  }

  function cornerBackground(data, W, H) {
    const samples = [];
    const box = Math.max(3, Math.round(Math.min(W, H) * 0.07));
    const push = (x, y) => {
      const i = (y * W + x) * 4;
      if (data[i + 3] > 64) samples.push([data[i], data[i + 1], data[i + 2]]);
    };
    for (let y = 0; y < box; y++) {
      for (let x = 0; x < box; x++) {
        push(x, y); push(W - 1 - x, y); push(x, H - 1 - y); push(W - 1 - x, H - 1 - y);
      }
    }
    if (!samples.length) return [240,240,240];
    const median = (k) => samples.map(v => v[k]).sort((a,b)=>a-b)[Math.floor(samples.length/2)];
    return [median(0), median(1), median(2)];
  }

  function analyzeBitmap(bitmap) {
    const W = 180;
    const H = Math.max(56, Math.min(120, Math.round(W * bitmap.height / bitmap.width)));
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, W, H);
    const { data } = ctx.getImageData(0, 0, W, H);
    const [br,bg,bb] = cornerBackground(data, W, H);

    const cols = new Float64Array(W);
    let total = 0;

    for (let y = 2; y < H - 2; y++) {
      // Slightly favor the vertical middle where necklaces/bracelets normally sit,
      // while still allowing objects near the top/bottom to influence the crop.
      const yn = y / (H - 1);
      const yWeight = 0.78 + 0.22 * (1 - Math.min(1, Math.abs(yn - 0.5) / 0.5));
      for (let x = 2; x < W - 2; x++) {
        const i = (y * W + x) * 4;
        if (data[i + 3] < 48) continue;
        const r=data[i], g=data[i+1], b=data[i+2];
        const lum = 0.2126*r + 0.7152*g + 0.0722*b;

        const ix1=(y*W+x+1)*4, ix0=(y*W+x-1)*4;
        const iy1=((y+1)*W+x)*4, iy0=((y-1)*W+x)*4;
        const gx = Math.abs(data[ix1]-data[ix0]) + Math.abs(data[ix1+1]-data[ix0+1]) + Math.abs(data[ix1+2]-data[ix0+2]);
        const gy = Math.abs(data[iy1]-data[iy0]) + Math.abs(data[iy1+1]-data[iy0+1]) + Math.abs(data[iy1+2]-data[iy0+2]);
        const edge = (gx + gy) / 6;

        const dr=r-br, dg=g-bg, db=b-bb;
        const bgDist = Math.sqrt(dr*dr + dg*dg + db*db);

        const max=Math.max(r,g,b), min=Math.min(r,g,b);
        const saturation = max ? (max-min)/max : 0;

        // Jewellery tends to be compact, contrasty and rich in small highlights/details.
        const detail = Math.max(0, edge - 8) * 1.9;
        const contrast = Math.max(0, bgDist - 14) * 0.42;
        const highlight = lum > 168 ? Math.min(38, (lum - 168) * 0.34) : 0;
        const chroma = saturation > 0.12 ? Math.min(18, saturation * 22) : 0;
        let score = (detail + contrast + highlight + chroma) * yWeight;

        // Suppress almost-flat background pixels.
        if (edge < 5 && bgDist < 22) score *= 0.15;
        // Prevent a single extreme edge from dominating.
        score = Math.min(score, 180);
        cols[x] += score;
        total += score;
      }
    }

    if (!Number.isFinite(total) || total < W * H * 1.8) return null;

    // Smooth column signal so a necklace/bracelet cluster wins over isolated noise.
    const smooth = new Float64Array(W);
    const radius = 5;
    for (let x=0; x<W; x++) {
      let s=0, n=0;
      for (let k=Math.max(0,x-radius); k<=Math.min(W-1,x+radius); k++) { s += cols[k]; n++; }
      smooth[x] = s/n;
    }
    const prefix = new Float64Array(W+1);
    for (let x=0; x<W; x++) prefix[x+1] = prefix[x] + smooth[x];
    return { W, prefix, total: prefix[W] };
  }

  function getAnalysis(img) {
    const src = img.currentSrc || img.src;
    if (!src) return Promise.resolve(null);
    if (analysisCache.has(src)) return analysisCache.get(src);
    const promise = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        try { resolve(analyzeBitmap(probe)); }
        catch (_) { resolve(null); }
      };
      probe.onerror = () => resolve(null);
      probe.src = src;
    });
    analysisCache.set(src, promise);
    return promise;
  }

  function scoreWindow(analysis, start, end) {
    const W = analysis.W;
    const a = Math.max(0, Math.min(W-1, Math.floor(start * W)));
    const b = Math.max(a+1, Math.min(W, Math.ceil(end * W)));
    return (analysis.prefix[b] - analysis.prefix[a]) / (b-a);
  }

  function chooseFocus(img, analysis) {
    if (!analysis) return 50;
    const rect = img.getBoundingClientRect();
    const sourceRatio = img.naturalWidth / img.naturalHeight;
    const targetRatio = rect.width / rect.height;
    const visibleFraction = Math.max(0.28, Math.min(1, targetRatio / sourceRatio));
    if (visibleFraction >= 0.94) return 50;

    // Evaluate several real cover-crop windows, not just three coarse positions.
    // This lets horizontal editorial photography follow the jewellery whether it
    // sits on the far left/right or only slightly off-centre.
    const candidates = [12, 28, 42, 50, 58, 72, 88];
    const scored = candidates.map((pct) => {
      let center = pct/100;
      let start = center - visibleFraction/2;
      let end = center + visibleFraction/2;
      if (start < 0) { end -= start; start = 0; }
      if (end > 1) { start -= (end-1); end = 1; }
      start = Math.max(0,start); end = Math.min(1,end);
      return { pct, score: scoreWindow(analysis,start,end) };
    });

    const centerScore = (scored.find((item) => item.pct === 50) || { score: 0 }).score;
    scored.sort((a,b)=>b.score-a.score);
    const best = scored[0];

    // Preserve a centred composition when the signal is essentially tied, but
    // move decisively when a side crop reveals materially more jewellery detail.
    if (best.pct !== 50 && best.score >= centerScore * 1.018) return best.pct;
    return 50;
  }

  function process(img, force = false) {
    if (!(img instanceof HTMLImageElement)) return;
    const ready = async () => {
      const forced = forcedFocusPct(img);
      if (forced !== null) {
        apply(img, forced);
        img.dataset.marriSmartFocus = forced === 100 ? 'right-locked' : 'python-right-soft';
        return;
      }
      if (!eligible(img)) return;
      if (hasRealFocalPoint(img)) {
        const raw = inlineAuthoredFocus(img);
        img.style.setProperty('object-position', raw, 'important');
        img.dataset.marriSmartFocus = 'authored';
        return;
      }

      const src = img.currentSrc || img.src;
      const rect = img.getBoundingClientRect();
      const signature = `${src}|${Math.round(rect.width)}x${Math.round(rect.height)}`;
      if (!force && applied.get(img) === signature) return;
      applied.set(img, signature);

      const analysis = await getAnalysis(img);
      const focus = chooseFocus(img, analysis);
      if (img.isConnected) apply(img, focus);
    };

    if (img.complete && img.naturalWidth) requestAnimationFrame(ready);
    else img.addEventListener('load', () => requestAnimationFrame(ready), { once: true });
  }

  function scan(root = document, force = false) {
    let images = [];
    if (root instanceof HTMLImageElement) images = [root];
    else images = Array.from(root.querySelectorAll?.(ROOT_SELECTOR) || []);
    images.forEach(img => process(img, force));
  }

  const idle = (fn) => 'requestIdleCallback' in window
    ? requestIdleCallback(fn, { timeout: 700 })
    : setTimeout(fn, 60);

  const start = () => idle(() => scan(document));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once:true });
  else start();

  const observer = new MutationObserver((records) => {
    idle(() => records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === 1) scan(node);
    })));
  });
  observer.observe(document.documentElement, { childList:true, subtree:true });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => scan(document, true), 180);
  }, { passive:true });
})();
